// File: apps/web/src/lib/api/collector-keys.ts
// Version: 2026.4.1 | Initial — Phase 2 Wave 2 collector-key hooks
//
// Mirrors the conventions in apps/web/src/lib/api/import.ts:
//   • useApiClient().createApi() returns an axios instance with the
//     Entra JWT auto-injected
//   • Read hooks: { data, loading, error, refetch }
//   • Mutation hooks: { mutate, loading, error } returning a Promise<T | null>

import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type { ApiError } from './types';
import type {
  CollectorKeySummary,
  MintedCollectorKey,
  SubscriptionLimitsResponse,
  CreateCollectorKeyRequest,
  RevokeCollectorKeyRequest,
} from './types-collector-keys';

// ─── Local ApiError flattening (matches import.ts toApiError shape) ─────────

function toApiError(err: any): ApiError {
  const envMessage =
    err?.response?.data?.error?.message
    && typeof err.response.data.error.message === 'string'
      ? (err.response.data.error.message as string)
      : null;

  return {
    message: envMessage
          ?? err?.response?.data?.message
          ?? err?.response?.data?.error
          ?? err?.message
          ?? 'Request failed',
    status:  err?.response?.status ?? 500,
    errors:  err?.response?.data?.errors,
  };
}

// ─── List keys ──────────────────────────────────────────────────────────────

export function useCollectorKeys(includeRevoked: boolean = false) {
  const { createApi } = useApiClient();
  const [data,    setData]    = useState<CollectorKeySummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = await createApi();
      const response = await api.get<CollectorKeySummary[]>(
        '/api/v1/settings/collector-keys',
        { params: { includeRevoked } });
      setData(response.data);
    } catch (err: any) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [createApi, includeRevoked]);

  useEffect(() => { void fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ─── Subscription limits ────────────────────────────────────────────────────

export function useSubscriptionLimits() {
  const { createApi } = useApiClient();
  const [data,    setData]    = useState<SubscriptionLimitsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = await createApi();
      const response = await api.get<SubscriptionLimitsResponse>(
        '/api/v1/settings/subscription-limits');
      setData(response.data);
    } catch (err: any) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => { void fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
}

// ─── Mutations: mint / revoke / rotate ──────────────────────────────────────

export function useCollectorKeyMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<ApiError | null>(null);

  const mint = useCallback(
    async (request: CreateCollectorKeyRequest): Promise<MintedCollectorKey | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<MintedCollectorKey>(
          '/api/v1/settings/collector-keys',
          request);
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]);

  const revoke = useCallback(
    async (id: number, request: RevokeCollectorKeyRequest): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        await api.post(`/api/v1/settings/collector-keys/${id}/revoke`, request);
        return true;
      } catch (err: any) {
        setError(toApiError(err));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [createApi]);

  const rotate = useCallback(
    async (id: number): Promise<MintedCollectorKey | null> => {
      setLoading(true);
      setError(null);
      try {
        const api = await createApi();
        const response = await api.post<MintedCollectorKey>(
          `/api/v1/settings/collector-keys/${id}/rotate`);
        return response.data;
      } catch (err: any) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [createApi]);

  return { mint, revoke, rotate, loading, error };
}
